import React from "react";
import { Helmet } from "react-helmet-async";

/**
 * Drop <SEO /> into any page component to set per-page meta tags.
 * Falls back to sensible Vision Security defaults.
 *
 * Props:
 *   title          – page title (appended with "| Vision Security")
 *   description    – meta description
 *   canonical      – path e.g. "/toronto"
 *   lang           – "en" | "fr"
 *   schema         – JSON-LD object OR array of JSON-LD objects
 *   image          – og:image path or absolute URL (defaults to site-wide card)
 *   imageAlt       – description of `image` (og:image:alt / twitter:image:alt)
 *   imageWidth / imageHeight – size of `image` in pixels, when known
 *   type           – og:type, "website" (default) or "article"
 *   publishedTime  – ISO date → article:published_time when type="article"
 *   modifiedTime   – ISO date → article:modified_time when type="article"
 *   noFrAlternate  – set true for pages that have no /fr counterpart (city pages)
 *   enAltPath      – explicit EN path for hreflang (use when FR slug is localized)
 *   frAltPath      – explicit FR path for hreflang (use when FR slug is localized)
 *   alternates     – { en?: "/path", fr?: "/fr/path" } — equivalent to enAltPath/frAltPath
 */
export default function SEO({
  title,
  description,
  canonical,
  lang = "en",
  schema,
  image,
  imageAlt,
  imageWidth,
  imageHeight,
  type = "website",
  publishedTime,
  modifiedTime,
  noFrAlternate = false,
  noIndex = false,
  enAltPath,
  frAltPath,
  alternates,
}) {
  const BASE_URL = "https://www.securityvision.ca";
  // Site-wide social card: the logo (180x191) until a 1200x630 public/og-default.jpg ships.
  const OG_IMAGE = `${BASE_URL}/VectorVisionLogo3.png`;
  const ogImage = image
    ? (/^https?:\/\//i.test(image) ? image : `${BASE_URL}${image}`)
    : OG_IMAGE;
  const ogImageAlt = (image && imageAlt) || "Vision Security — Licensed Security Company Canada";
  // Declare the size only when it is known (a wrong size makes networks crop or skip the image)
  const ogSize = image ? (imageWidth && imageHeight ? [imageWidth, imageHeight] : null) : [180, 191];
  const isArticle = type === "article";
  const defaultTitle = "Vision Security | Security Guards Quebec & Ontario";
  const defaultDesc  = "Licensed security company serving Toronto, Montreal, Ottawa and Quebec City. PSISA-licensed in Ontario, BSP-licensed in Quebec. 24/7 guards, mobile patrol, event security and surveillance.";

  const brandSuffix  = lang === "fr" ? "Vision Sécurité" : "Vision Security";
  // Avoid double-brand: if the incoming title already ends with/contains the brand, use as-is.
  const alreadyBranded = title && /vision\s+s(e|é)curit(y|é)/i.test(title);
  const finalTitle = title
    ? (alreadyBranded ? title : `${title} | ${brandSuffix}`)
    : defaultTitle;
  const finalDesc  = description || defaultDesc;
  const finalUrl   = canonical  ? `${BASE_URL}${canonical}` : BASE_URL;

  // Normalize: support both `alternates={{en,fr}}` and `enAltPath`/`frAltPath`.
  const enOverride = enAltPath || (alternates && alternates.en);
  const frOverride = frAltPath || (alternates && alternates.fr);

  // Derive EN path for hreflang (strip /fr prefix if present)
  const enPath = canonical ? canonical.replace(/^\/fr(\/|$)/, "/") : "";
  const autoEnUrl  = `${BASE_URL}${enPath === "/" && canonical !== "/" ? "" : enPath}`;
  const autoFrUrl  = `${BASE_URL}/fr${enPath === "/" ? "" : enPath}`;

  // Allow explicit EN/FR overrides for pages with localized slugs.
  const enUrl = enOverride ? `${BASE_URL}${enOverride}` : autoEnUrl;
  const frUrl = frOverride ? `${BASE_URL}${frOverride}` : autoFrUrl;

  // When `alternates` is passed explicitly, only emit hreflang for the keys present.
  const hasFrAlternate = !noFrAlternate && (alternates ? !!alternates.fr : true);
  const hasEnAlternate = alternates ? !!alternates.en : true;

  // Support schema as a single object or an array
  const schemas = schema ? (Array.isArray(schema) ? schema : [schema]) : [];

  return (
    <Helmet>
      <html lang={lang} />
      <title>{finalTitle}</title>
      <meta name="description"   content={finalDesc} />
      <meta name="robots"        content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical"      href={finalUrl} />

      {/* Open Graph */}
      <meta property="og:title"        content={finalTitle} />
      <meta property="og:description"  content={finalDesc} />
      <meta property="og:url"          content={finalUrl} />
      <meta property="og:type"         content={type} />
      <meta property="og:image"        content={ogImage} />
      {ogSize && <meta property="og:image:width"  content={String(ogSize[0])} />}
      {ogSize && <meta property="og:image:height" content={String(ogSize[1])} />}
      <meta property="og:image:alt"    content={ogImageAlt} />
      <meta property="og:locale"       content={lang === "fr" ? "fr_CA" : "en_CA"} />
      <meta property="og:site_name"    content="Vision Security" />
      {isArticle && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {isArticle && (modifiedTime || publishedTime) && (
        <meta property="article:modified_time" content={modifiedTime || publishedTime} />
      )}

      {/* Twitter Card */}
      {/* The logo card is small: a large-image card only when the page has its own image */}
      <meta name="twitter:card"        content={image ? "summary_large_image" : "summary"} />
      <meta name="twitter:title"       content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image"       content={ogImage} />
      <meta name="twitter:image:alt"   content={ogImageAlt} />

      {/* Hreflang â€” correct EN/FR alternates regardless of current language */}
      {hasEnAlternate && (
        <link rel="alternate" hreflang="en-ca"    href={enUrl} />
      )}
      {hasFrAlternate && (
        <link rel="alternate" hreflang="fr-ca"  href={frUrl} />
      )}
      <link rel="alternate" hreflang="x-default" href={enUrl} />

      {/* JSON-LD structured data â€” one <script> per schema object */}
      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
